import {useGrpcRequestCallback} from '@aboutyou/data-fetcher';
import {NewsletterSubscriptionService_SubscribeToNewsletterLazy} from '@aboutyou/grpc-clients/tadarida/aysa_api/services/newsletter_subscription/v1/service.grpc.lazy';
import {Gender} from '@aboutyou/grpc-clients/tadarida/aysa_api/services/newsletter_subscription/v1/service.types';
import {useTranslation} from '@aboutyou/i18n';
import {Log} from '@aboutyou/mlm';
import {useIsPersonalisationConsentGiven} from '@aboutyou/storage';
import {useShowToast} from '@aboutyou/ui-components';
import {useOptionalSession, useUserEmail} from '@aboutyou/user';
import {startTransition, useState} from 'react';
import {useForm} from 'react-hook-form';
import {
  SessionGender,
  useSessionGender,
} from '../../../../shared-deprecated/sessionGender';
import {useNewsletterFlyoutStateManager} from './useNewsletterFlyoutStateManager';

export type OspSubscribeToNewsletterFormType = {
  email: string;
  gender: SessionGender;
};

export type NewsletterSubscriptionResponseType = {
  type: 'success' | 'error';
  text: string;
};

export const useNewsletterFormHandler = ({
  showErrorMessage,
  newlySubscribedConfirmMessage,
}: {
  showErrorMessage: boolean;
  newlySubscribedConfirmMessage: string;
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [newsletterSubscriptionResponse, setNewsletterSubscriptionResponse] =
    useState<NewsletterSubscriptionResponseType>();
  const gender = useSessionGender().gender;
  const userEmail = useUserEmail();
  const isPersonalisationConsentGiven = useIsPersonalisationConsentGiven();

  const {
    register,
    handleSubmit,
    formState: {errors},
    getValues,
  } = useForm<OspSubscribeToNewsletterFormType>({
    mode: 'onSubmit',
    values: {
      email: isPersonalisationConsentGiven && userEmail ? userEmail : '',
      gender: gender === 'male' ? 'male' : 'female',
    },
  });

  const submitHandler = useSubscribeToNewsletter({
    setIsSubmitting,
    setNewsletterSubscriptionResponse,
    showErrorMessage,
    newlySubscribedConfirmMessage,
  });

  return {
    register,
    submitHandler: handleSubmit(submitHandler),
    newsletterSubscriptionResponse,
    errors,
    isSubmitting,
    getValues,
  };
};

const useSubscribeToNewsletter = ({
  setNewsletterSubscriptionResponse,
  setIsSubmitting,
  showErrorMessage,
  newlySubscribedConfirmMessage,
}: {
  setNewsletterSubscriptionResponse: (
    params: NewsletterSubscriptionResponseType,
  ) => void;
  setIsSubmitting: (isSubmitting: boolean) => void;
  showErrorMessage: boolean;
  newlySubscribedConfirmMessage: string;
}) => {
  const {t} = useTranslation();
  const session = useOptionalSession();
  const showToast = useShowToast();
  const {cancel} = useNewsletterFlyoutStateManager();

  const subscribeToNewsletter = useGrpcRequestCallback(
    NewsletterSubscriptionService_SubscribeToNewsletterLazy,
  );

  const submitHandler = ({email, gender}: OspSubscribeToNewsletterFormType) => {
    startTransition(() => {
      setIsSubmitting(true);
    });

    subscribeToNewsletter({
      email,
      gender: gender === 'male' ? Gender.MALE : Gender.FEMALE,
      session,
    })
      .then(() => {
        const successMessageData: NewsletterSubscriptionResponseType = {
          type: 'success',
          text: newlySubscribedConfirmMessage,
        };

        if (showErrorMessage) {
          startTransition(() => {
            cancel();
            setIsSubmitting(false);

            // showing the success message after the flyout finishes closing
            setTimeout(() => {
              setNewsletterSubscriptionResponse(successMessageData);
            }, 350);
          });
        } else {
          startTransition(() => {
            setNewsletterSubscriptionResponse(successMessageData);
          });
        }
      })
      .catch((error: Error) => {
        Log.error('@aboutyou/logic-components', 'useSubscribeToNewsletter', {
          error,
        });

        if (showErrorMessage) {
          startTransition(() => {
            cancel();
            showToast({
              content: t(
                'Sorry, something went wrong! Please try again later.',
              ),
              type: 'ERROR',
            });
            setIsSubmitting(false);
          });
        } else {
          startTransition(() => {
            setNewsletterSubscriptionResponse({
              type: 'error',
              text: t('Sorry, something went wrong! Please try again later.'),
            });
          });
        }
      });
  };

  return submitHandler;
};
